import { DeleteIcon, DragIndicatorIcon } from '@/assets/icons'
import { iconSizes } from '@/theme/tokens'
import { UniqueIdentifier } from '@dnd-kit/core'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import React from 'react'
import {
	DragHandle,
	RemoveButton,
	Row,
	RowBand,
	RowControls,
	RowError,
	RowMeta,
	RowName,
	RowText,
} from './SortableRow.styles'

/** The words a row is made of, which its drag overlay repeats. */
interface RowContent {
	name: string
	/** A line under the name — "3 events", "9:30 AM". */
	meta?: string | null
}

export interface SortableRowProps extends RowContent {
	/** The row's id in its `SortableContext`. */
	id: UniqueIdentifier
	/** What the caller adds between the text and the delete button — a stepper, say. */
	controls?: React.ReactNode
	/** An error under the band, when the row's edit can't be saved. */
	error?: string
	/** The delete button's accessible name, naming the row: "Remove Tokyo". */
	removeLabel: string
	/** The drag handle's accessible name, naming the row: "Reorder Tokyo". */
	reorderLabel: string
	/**
	 * Drag from anywhere on the band, not only the handle — for a row with
	 * nothing else to press. Pair with `useSortableRowSensors({ holdOnTouch: true })`,
	 * so a swipe across the rows still scrolls the list. The handle stays, as the
	 * affordance and as what the keyboard lifts.
	 */
	dragAnywhere?: boolean
	onRemove: () => void
	/** The row's e2e test ID (see `@/testIds`). */
	testId?: string
}

/**
 * The draggable, deletable row: a name over an optional meta line on a blue
 * band, the caller's own controls, delete, and the drag handle that moves it
 * (pointer or keyboard, via dnd-kit) — the handle alone, so the controls stay
 * clickable, unless the caller opens the whole band with `dragAnywhere`. Both buttons are always visible: nothing here waits
 * for a hover, which touch does not have. Lives inside a `SortableContext`.
 */
const SortableRow: React.FC<SortableRowProps> = ({
	id,
	name,
	meta,
	controls,
	error,
	removeLabel,
	reorderLabel,
	dragAnywhere,
	onRemove,
	testId,
}) => {
	const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
		id,
	})

	return (
		<Row
			ref={setNodeRef}
			data-testid={testId}
			$transform={CSS.Transform.toString(transform)}
			$transition={transition}
			$isDragging={isDragging}
		>
			<RowBand $grabbable={dragAnywhere} {...(dragAnywhere ? listeners : undefined)}>
				<RowText>
					<RowName>{name}</RowName>
					{meta && <RowMeta>{meta}</RowMeta>}
				</RowText>
				<RowControls>
					{controls}
					<RemoveButton
						type="button"
						aria-label={removeLabel}
						// A press on delete is never the start of a drag.
						onPointerDown={event => event.stopPropagation()}
						onMouseDown={event => event.stopPropagation()}
						onTouchStart={event => event.stopPropagation()}
						onClick={onRemove}
					>
						<DeleteIcon sx={{ fontSize: iconSizes.md }} />
					</RemoveButton>
					<DragHandle ref={setActivatorNodeRef} type="button" aria-label={reorderLabel} {...attributes} {...(dragAnywhere ? undefined : listeners)}>
						<DragIndicatorIcon sx={{ fontSize: iconSizes.md }} />
					</DragHandle>
				</RowControls>
			</RowBand>
			{error && <RowError role="alert">{error}</RowError>}
		</Row>
	)
}

/**
 * The row as it looks in the air, for a `DragOverlay`: the same band with
 * nothing wired. A list whose rows cross between sortable contexts drags this
 * instead of the row itself, which unmounts from one context to mount in the
 * next.
 */
export const SortableRowOverlay: React.FC<RowContent> = ({ name, meta }) => (
	<Row as="div" $isDragging={false} aria-hidden="true">
		<RowBand>
			<RowText>
				<RowName>{name}</RowName>
				{meta && <RowMeta>{meta}</RowMeta>}
			</RowText>
			<RowControls>
				<RemoveButton as="span">
					<DeleteIcon sx={{ fontSize: iconSizes.md }} />
				</RemoveButton>
				<DragHandle as="span">
					<DragIndicatorIcon sx={{ fontSize: iconSizes.md }} />
				</DragHandle>
			</RowControls>
		</RowBand>
	</Row>
)

export default SortableRow
